import {SpineResourceConfig, Urls} from "@/constants/urls";
import {Keys} from "@/constants/key";
import CU from "@/utils/c-u";
import PlayerUtils from "@/utils/player-utils";
import QualityUtils from "@/utils/quality-utils";
import Group = Phaser.GameObjects.Group;
import Scene = Phaser.Scene;

const SKELETAL_ANIMATIONS_DISABLED_VERSIONS = [11, 13, 14, 15, 16, 17];
// В 14-й версии единственная разрешённая скелетная анимация - загрузочный баннер.
const LOAD_SCREEN_ONLY_VERSION = 14;

type ResolvedSpineConfig = SpineResourceConfig & {
    resourceKey: string;
    dataKey: string;
    atlasKey: string;
};

type SpineAddOptions = {
    animation?: string;
    scale?: number;
    originX?: number;
    originY?: number;
    depth?: number;
    group?: Group;
    onLoadComplete?: () => void;
};

type PlaceVisualRef = {
    atlasId?: string | undefined;
    imageId?: string | undefined;
};

export default class SpineUtils {
    private static queuedSpineKeys: WeakMap<Scene, Set<string>> = new WeakMap<Scene, Set<string>>();
    private static game: any;
    private static canvasRenderer: boolean | undefined;

    /** Вызывается из game.ts сразу после создания Phaser.Game. */
    public static bindGame(game: any): void {
        this.game = game;
        this.canvasRenderer = undefined;
    }

    // На canvas-рендерере spine рисует меши по одному треугольнику (save/clip/transform/drawImage
    // на каждый), это в разы дороже WebGL-батча, а часть возможностей (зеркалирование, клиппинг,
    // блендмоды) canvas-путь вообще не поддерживает. Поэтому там откатываемся на статичные спрайты
    // так же, как на LOW/LOWEST.
    private static usingCanvasRenderer(): boolean {
        if (this.canvasRenderer !== undefined) {
            return this.canvasRenderer;
        }

        const renderType = this.game?.renderer?.type ?? this.game?.config?.renderType;
        if (renderType === Phaser.WEBGL || renderType === Phaser.CANVAS) {
            this.canvasRenderer = renderType === Phaser.CANVAS;
            return this.canvasRenderer;
        }

        // Игра ещё не забутилась (на cordova boot ждёт deviceready), AUTO не разрешён в конкретный
        // рендерер. Спрашиваем ту же фичу, по которой этот выбор сделает сам Phaser (CreateRenderer).
        // Ответ не кэшируем и при неизвестности считаем, что рендерер WebGL - как только игра
        // забутится, ответ станет точным, а до этого скелетки всё равно никто не создаёт.
        return this.game?.device?.features?.webGL === false;
    }

    private static getQueuedKeys(scene: Scene): Set<string> {
        let queued = this.queuedSpineKeys.get(scene);
        if (!queued) {
            queued = new Set<string>();
            this.queuedSpineKeys.set(scene, queued);
        }
        return queued;
    }

    private static buildResourceKey(texture: string, frame?: string): string {
        return frame ? `${texture}:${frame}` : texture;
    }

    private static buildCacheKey(resourceKey: string, suffix: "data" | "atlas"): string {
        return `spine:${suffix}:${resourceKey.replace(/[^a-zA-Z0-9:_/-]/g, "_")}`;
    }

    private static queueLoad(scene: Scene, config: ResolvedSpineConfig): boolean {
        const loadPlugin = scene.load as any;
        if (typeof loadPlugin.spineBinary !== "function" || typeof loadPlugin.spineAtlas !== "function") {
            return false;
        }

        const queuedKeys = this.getQueuedKeys(scene);
        const queueKey = `${config.dataKey}|${config.atlasKey}`;
        let queued = false;

        if (!queuedKeys.has(queueKey)) {
            if (!scene.cache.binary.exists(config.dataKey)) {
                loadPlugin.spineBinary(config.dataKey, config.dataPath);
                queued = true;
            }
            if (!scene.cache.text.exists(config.atlasKey)) {
                loadPlugin.spineAtlas(config.atlasKey, config.atlasPath);
                queued = true;
            }

            if (queued) {
                queuedKeys.add(queueKey);
            }
        }

        return queued;
    }

    private static skeletalAnimationsEnabled(texture: string): boolean {
        // На LOW/LOWEST скелетные анимации не создаём - вызывающий код сам откатится
        // на статичный спрайт, т.к. везде проверяет SpineUtils.has().
        if (QualityUtils.isLowQuality()) {
            return false;
        }

        if (this.usingCanvasRenderer()) {
            return false;
        }

        const version = PlayerUtils.getStartVersion();
        if (!SKELETAL_ANIMATIONS_DISABLED_VERSIONS.includes(version)) {
            return true;
        }
        return version === LOAD_SCREEN_ONLY_VERSION && texture === Keys.IMG_LOAD_SCREEN_BACKGROUND;
    }

    public static resolveConfig(texture: string, frame?: string): ResolvedSpineConfig | undefined {
        if (!texture || !this.skeletalAnimationsEnabled(texture)) {
            return undefined;
        }

        const resourceKey = this.buildResourceKey(texture, frame);
        const baseConfig = Urls.SPINES.get(resourceKey) ?? Urls.SPINES.get(texture);
        if (!baseConfig) {
            return undefined;
        }

        const configKey = Urls.SPINES.has(resourceKey) ? resourceKey : texture;
        return {
            ...baseConfig,
            resourceKey: configKey,
            dataKey: this.buildCacheKey(configKey, "data"),
            atlasKey: this.buildCacheKey(configKey, "atlas"),
        };
    }

    public static has(texture: string, frame?: string): boolean {
        return Boolean(this.resolveConfig(texture, frame));
    }

    public static preloadPlaceVisuals(scene: Scene, visuals: PlaceVisualRef[], startIfNeeded: boolean = false): boolean {
        const loadPlugin = scene.load as any;
        let hasPending = false;

        for (const visual of visuals) {
            const texture = visual.atlasId || visual.imageId;
            if (!texture) {
                continue;
            }

            const frame = visual.atlasId ? visual.imageId : undefined;
            const config = this.resolveConfig(texture, frame);
            if (!config) {
                continue;
            }

            if (!scene.cache.binary.exists(config.dataKey) || !scene.cache.text.exists(config.atlasKey)) {
                hasPending = true;
            }
            this.queueLoad(scene, config);
        }

        if (startIfNeeded && hasPending && typeof loadPlugin?.isLoading === "function" && !loadPlugin.isLoading()) {
            loadPlugin.start();
        }

        return hasPending;
    }

    public static isLoaded(scene: Scene, texture: string, frame?: string): boolean {
        const config = this.resolveConfig(texture, frame);
        if (!config) {
            return false;
        }

        return scene.cache.binary.exists(config.dataKey) && scene.cache.text.exists(config.atlasKey);
    }

    public static ensureLoaded(scene: Scene, texture: string, frame?: string, onLoadComplete?: () => void): boolean {
        const config = this.resolveConfig(texture, frame);
        const loadPlugin = scene.load as any;
        if (!config || typeof loadPlugin.spineBinary !== "function" || typeof loadPlugin.spineAtlas !== "function") {
            return false;
        }

        if (this.isLoaded(scene, texture, frame)) {
            return true;
        }

        if (onLoadComplete) {
            scene.load.once(Phaser.Loader.Events.COMPLETE, onLoadComplete);
        }

        const queued = this.queueLoad(scene, config);

        if ((queued || loadPlugin.isLoading()) && !loadPlugin.isLoading()) {
            loadPlugin.start();
        }

        return false;
    }

    // SpineGameObject наследует стандартный Alpha-миксин Phaser (alpha/setAlpha), но собственный
    // renderWebGL рисует skeleton напрямую через drawSkeleton() и полностью игнорирует this.alpha —
    // поэтому твины alpha (FADE/UNFADE и т.п.) визуально ничего не делают. Синхронизируем каждый кадр
    // skeleton.color.a с эффективной альфой объекта (учитывая альфу родительских контейнеров, т.к.
    // Container прокидывает альфу детям только через временные _alphaTL/TR/BL/BR, не трогая .alpha).
    private static patchAlphaSync(spineObject: any): void {
        if (!spineObject || typeof spineObject.preUpdate !== "function") {
            return;
        }

        const originalPreUpdate = spineObject.preUpdate.bind(spineObject);
        const syncAlpha = () => {
            const skeleton = spineObject.skeleton;
            if (!skeleton || !skeleton.color) {
                return;
            }

            let effectiveAlpha = spineObject.alpha;
            let parent = spineObject.parentContainer;
            while (parent) {
                effectiveAlpha *= parent.alpha;
                parent = parent.parentContainer;
            }

            skeleton.color.a = effectiveAlpha;
        };

        spineObject.preUpdate = (time: number, delta: number) => {
            originalPreUpdate(time, delta);
            syncAlpha();
        };

        syncAlpha();
    }

    public static addSpine(scene: Scene, x: number, y: number, texture: string, frame?: string, options?: SpineAddOptions): any | undefined {
        const config = this.resolveConfig(texture, frame);
        if (!config) {
            return undefined;
        }

        if (!this.isLoaded(scene, texture, frame)) {
            this.ensureLoaded(scene, texture, frame, options?.onLoadComplete);
            return undefined;
        }

        const spinePlugin = (scene as any).spine;
        if (typeof (scene.add as any).spine !== "function" || !spinePlugin || typeof spinePlugin.getSkeletonData !== "function") {
            return undefined;
        }

        const spineObject = (scene.add as any).spine((x + (config.offsetX ?? 0)) * CU.S, y * CU.S, config.dataKey, config.atlasKey);

        this.patchAlphaSync(spineObject);

        if (typeof spineObject?.setOrigin === "function") {
            spineObject.setOrigin(options?.originX ?? 0.5, options?.originY ?? 0.5);
        }

        if (typeof spineObject?.setScale === "function") {
            spineObject.setScale(CU.S * (options?.scale ?? config.defaultScale ?? 1));
        }

        if (options?.depth != null && typeof spineObject?.setDepth === "function") {
            spineObject.setDepth(options.depth);
        }

        options?.group?.add(spineObject);

        const skeletonData = spinePlugin.getSkeletonData(config.dataKey, config.atlasKey);
        const animations = skeletonData?.animations || spineObject?.skeleton?.data?.animations || [];
        const startAnimationName = animations.find((animation: any) => animation?.name === '1-start')?.name;
        const idleAnimationName = animations.find((animation: any) => animation?.name === '2-idle')?.name;
        const animationName = animations.find((animation: any) => animation?.name === (options?.animation ?? config.defaultAnimation))?.name
            ?? animations[0]?.name;

        if (animationName && typeof spineObject?.animationState?.setAnimation === "function") {
            if (startAnimationName && idleAnimationName && typeof spineObject.animationState.addAnimation === "function") {
                spineObject.animationState.setAnimation(0, startAnimationName, false);
                spineObject.animationState.addAnimation(0, idleAnimationName, true, 0);
            } else {
                spineObject.animationState.setAnimation(0, animationName, true);
            }
        }

        return spineObject;
    }
}
